import Link from "next/link";
import type { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { BoroughDeliveryEstimator } from "@/components/borough-delivery-estimator";
import { ContentPageBlocks } from "@/components/content-page-blocks";
import { DeliveryQuotePage } from "@/components/delivery-quote-page";
import { ContentPageIntro } from "@/components/content-page-intro";
import { PageSeoHead } from "@/components/page-seo-head";
import {
  getBoroughDeliveryEstimate,
  getContentPage,
  getFooter,
  getMenuPage,
  getNavigation,
} from "@/lib/api";
import { deriveFreeDeliveryThresholds } from "@/lib/borough-delivery-estimator";
import {
  buildBoroughDeliveryServiceSchema,
  buildBreadcrumbList,
  buildFaqSchema,
  type BreadcrumbItem,
} from "@/lib/seo-schema";
import type {
  StorefrontLayoutVariant,
  StorefrontShellData,
} from "@/lib/storefront-shell-types";
import {
  getStorefrontCanonicalOrigin,
  getStorefrontSiteConfig,
  resolveStorefrontSiteCode,
  type StorefrontSiteCode,
} from "@/lib/storefront-site";
import type {
  BoroughDeliveryEstimate,
  ContentPageData,
  NavigationItem,
  PageLinkSummary,
} from "@/lib/storefront-types";

type DeliveryQuoteContext = {
  addressLine1: string;
  postcode: string;
};

type ContentPageRouteProps = {
  deliveryQuoteBoroughLinks: PageLinkSummary[];
  deliveryQuoteContext: DeliveryQuoteContext | null;
  initialDeliveryEstimate: BoroughDeliveryEstimate | null;
  layoutVariant: StorefrontLayoutVariant;
  navigationItems: NavigationItem[];
  page: ContentPageData;
  recaptchaSiteKey: string | null;
  shellData: StorefrontShellData;
  siteCode: StorefrontSiteCode;
};

const centeredSectionClassName = "mx-auto max-w-6xl px-4 sm:px-6";

function formatBoroughName(slug: string) {
  const lowerCaseJoiners = new Set(["and", "of", "upon"]);

  return slug
    .split("-")
    .filter(Boolean)
    .map((segment, index) => {
      const lower = segment.toLowerCase();
      if (index > 0 && lowerCaseJoiners.has(lower)) {
        return lower;
      }

      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

function getFirstQueryValue(value: string | string[] | undefined) {
  if (Array.isArray(value)) {
    return value[0];
  }

  return value;
}

/**
 * Breadcrumb trail from the page path. Intermediate crumb names are
 * title-cased from their slug (we only have the current page's title);
 * the final crumb uses the real page title.
 */
function buildContentPageCrumbs(page: ContentPageData): BreadcrumbItem[] {
  const segments = page.path.split("/").filter(Boolean);
  const crumbs: BreadcrumbItem[] = [{ name: "Home", path: "/" }];

  segments.forEach((segment, index) => {
    const path = `/${segments.slice(0, index + 1).join("/")}`;
    const isLast = index === segments.length - 1;
    crumbs.push({
      name: isLast ? page.title : formatBoroughName(segment),
      path,
    });
  });

  return crumbs;
}

/**
 * Borough landing pages are organic-search targets, so guarantee a keyword-rich
 * title + meta description even when a CMS editor leaves them blank — otherwise
 * they fall back to the generic brand description. CMS-authored values always win.
 */
function applyBoroughSeoFallback(
  seo: ContentPageData["seo"],
  boroughName: string,
  siteCode: StorefrontSiteCode,
): ContentPageData["seo"] {
  const { brandName } = getStorefrontSiteConfig(siteCode);
  const seoTitle =
    seo.seoTitle?.trim() ||
    seo.pageTitle?.trim() ||
    `Nigerian Food Delivery in ${boroughName} | ${brandName}`;
  const seoDescription =
    seo.seoDescription?.trim() ||
    `Order authentic Nigerian food for delivery in ${boroughName}, London. See live ${brandName} delivery prices — same-day on demand or a cheaper scheduled run, with free collection.`;

  return { ...seo, seoTitle, seoDescription };
}

export const getServerSideProps: GetServerSideProps<ContentPageRouteProps> = async (
  context,
) => {
  const siteCode = resolveStorefrontSiteCode({
    configuredSiteCode: process.env.STOREFRONT_SITE,
    hostname: context.req.headers.host,
  });
  const slug = context.params?.slug;
  if (!Array.isArray(slug) || slug.length === 0) {
    return { notFound: true };
  }

  const [navigation, menuPage, page, footer] = await Promise.all([
    getNavigation(siteCode),
    getMenuPage(siteCode),
    getContentPage(slug, siteCode),
    getFooter(siteCode),
  ]);

  if (!page) {
    return { notFound: true };
  }

  const londonHubPage =
    page.pageKind === "deliveryQuote"
      ? await getContentPage(["london"], siteCode)
      : null;
  const quoteAddressLine1 = getFirstQueryValue(context.query.addressLine1)?.trim();
  const quotePostcode = getFirstQueryValue(context.query.postcode)?.trim();

  const initialDeliveryEstimate =
    page.pageKind === "borough"
      ? await (async () => {
          const baseEstimate = await getBoroughDeliveryEstimate(page.path, 0, siteCode);
          const defaultSpend = baseEstimate.slider.defaultValue;
          const currentEstimate =
            defaultSpend <= baseEstimate.slider.min || defaultSpend === baseEstimate.spend
              ? baseEstimate
              : await getBoroughDeliveryEstimate(page.path, defaultSpend, siteCode);

          // Stamp each option's free-delivery threshold (preferring the CMS
          // value; deriving it from the £0-spend base estimate otherwise) so the
          // slider's free-delivery marker has a position to anchor to.
          return deriveFreeDeliveryThresholds(currentEstimate, baseEstimate);
        })()
      : null;

  const pageForProps =
    page.pageKind === "borough"
      ? {
          ...page,
          seo: applyBoroughSeoFallback(
            page.seo,
            formatBoroughName(page.slug),
            siteCode,
          ),
        }
      : page;

  return {
    props: {
      deliveryQuoteBoroughLinks: londonHubPage?.childPages ?? [],
      deliveryQuoteContext:
        page.pageKind === "borough" && quoteAddressLine1 && quotePostcode
          ? {
              addressLine1: quoteAddressLine1,
              postcode: quotePostcode,
            }
          : null,
      initialDeliveryEstimate,
      layoutVariant: "immersive",
      navigationItems: navigation.items,
      page: pageForProps,
      recaptchaSiteKey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? null,
      siteCode,
      shellData: {
        footer,
        menuCategories: menuPage.categories,
        currentPageTitle: page.title,
        loginRegisterInsertAfter: navigation.loginRegisterInsertAfter,
      },
    },
  };
};

export default function ContentPageRoute({
  deliveryQuoteBoroughLinks,
  deliveryQuoteContext,
  initialDeliveryEstimate,
  page,
  recaptchaSiteKey,
  siteCode,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const isLondonHub = page.path === "/london";
  const isLondonBoroughPage = page.path.startsWith("/london/") && !isLondonHub;
  const isDeliveryQuotePage = page.pageKind === "deliveryQuote";
  const isFrozenDeliveryQuotePage =
    isDeliveryQuotePage && siteCode === "asorockfrozen";
  const isMorePage = page.path === "/more";
  const isMoreDeliveryQuotePage = page.path === "/more/delivery-quote";
  const boroughName = isLondonBoroughPage ? formatBoroughName(page.slug) : null;
  const showChildPageGrid = page.childPages.length > 0 && !isLondonHub;
  const contentBlocks = isMoreDeliveryQuotePage
    ? page.blocks.filter((block) => block.kind !== "londonBoroughs")
    : page.blocks;

  const origin = getStorefrontCanonicalOrigin(siteCode);
  const faqSchema = buildFaqSchema(contentBlocks);
  const structuredData: object[] = [
    buildBreadcrumbList(origin, buildContentPageCrumbs(page)),
    ...(faqSchema ? [faqSchema] : []),
    // Fresh-only: PageKind is purely doc-type-driven in the CMS (no site
    // check), so a borough landing page created under the Frozen site would
    // otherwise emit a Restaurant-provider Service node on a brand that is
    // an OnlineStore, against the wrong #organization node.
    ...(page.pageKind === "borough" && siteCode === "asorockfood"
      ? [
          buildBoroughDeliveryServiceSchema(
            origin,
            getStorefrontSiteConfig(siteCode).brandName,
            boroughName ?? page.title,
            page.path,
          ),
        ]
      : []),
  ];

  return (
    <>
      <PageSeoHead
        siteCode={siteCode}
        seo={page.seo}
        ogType={isLondonBoroughPage ? "article" : "website"}
        structuredData={structuredData}
      />
      <div
        className={
          isFrozenDeliveryQuotePage
            ? "bg-[#f7fbff]"
            : isDeliveryQuotePage || isLondonBoroughPage
              ? "bg-[var(--storefront-primary-dark)]"
              : `storefront-page-backdrop ${isMorePage ? "pb-10 sm:pb-14" : ""}`
        }
      >
        {isDeliveryQuotePage && !isFrozenDeliveryQuotePage ? (
          <DeliveryQuotePage
            boroughLinks={deliveryQuoteBoroughLinks}
            recaptchaSiteKey={recaptchaSiteKey}
            title={page.title}
          />
        ) : !isDeliveryQuotePage && !isLondonBoroughPage ? (
          <ContentPageIntro
            title={page.title}
            description={page.seo.seoDescription || null}
            image={page.headerImage}
          />
        ) : null}
        {page.pageKind === "borough" && initialDeliveryEstimate ? (
          <BoroughDeliveryEstimator
            boroughName={boroughName ?? page.title}
            boroughLogo={page.logo}
            deliveryQuoteContext={deliveryQuoteContext}
            initialEstimate={initialDeliveryEstimate}
            pagePath={page.path}
          />
        ) : null}
        {showChildPageGrid ? (
          <div className={isMorePage ? `${centeredSectionClassName} storefront-surface-dark rounded-[2rem] mt-10 py-8 sm:mt-14 sm:py-10` : centeredSectionClassName}>
            <section className={isMorePage ? "overflow-hidden rounded-[2rem]" : "storefront-page-panel-soft overflow-hidden rounded-[2rem]"}>
              <div className="px-6 py-6 sm:px-8">
                <h2 className={isMorePage ? "text-2xl font-semibold tracking-tight text-white sm:text-[2rem]" : "storefront-page-title text-2xl font-semibold tracking-tight sm:text-[2rem]"}>
                  Related Pages
                </h2>
                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {page.childPages.map((childPage) => (
                    <Link
                      key={childPage.path}
                      href={childPage.path}
                      className={
                        isMorePage
                          ? "storefront-page-card-inverse-soft rounded-[1.6rem] px-5 py-6 transition-[border-color] duration-200 hover:border-white/20"
                          : "storefront-page-card storefront-page-card-interactive rounded-[1.6rem] px-5 py-6"
                      }
                    >
                      <h3 className={isMorePage ? "text-base font-semibold uppercase tracking-[0.16em] text-white" : "storefront-page-title text-base font-semibold uppercase tracking-[0.16em]"}>
                        {childPage.label}
                      </h3>
                      {childPage.summary ? (
                        <p className={isMorePage ? "storefront-page-body-inverse mt-3 text-sm leading-6" : "storefront-page-body mt-3 text-sm leading-6"}>
                          {childPage.summary}
                        </p>
                      ) : null}
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          </div>
        ) : null}
        {contentBlocks.length > 0 ? (
          <ContentPageBlocks
            blocks={contentBlocks}
            frozenDeliveryCalculatorHeadingLevel={
              isFrozenDeliveryQuotePage ? 1 : 2
            }
            pageAppearance={
              isLondonBoroughPage ||
              (isDeliveryQuotePage && !isFrozenDeliveryQuotePage)
                ? undefined
                : "light"
            }
          />
        ) : null}
      </div>
    </>
  );
}
